import { Check, ChevronDown, Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Flag from './Flag';
import { useLanguage } from '../i18n';
import type { Lang } from '../i18n';

/** Each language named in itself, because that is what a reader looking for it will recognise. */
const OPTIONS: [{ value: Lang; label: string; name: string }, ...Array<{ value: Lang; label: string; name: string }>] = [
  { value: 'en', label: 'EN', name: 'English' },
  { value: 'tr', label: 'TR', name: 'Türkçe' },
  { value: 'pl', label: 'PL', name: 'Polski' },
  { value: 'pt', label: 'PT', name: 'Português' },
  { value: 'es', label: 'ES', name: 'Español' },
];

/**
 * The language chooser in the header: a flag, the language's name, and a list of the rest.
 *
 * <p>A flag is a country, not a language, so it never identifies an option on its own - the name
 * is beside it, written in that language, and the button carries the name too wherever there is
 * room for it. A native select cannot draw a flag in an option, so this is a button and a list of
 * buttons; the arrow keys move between them, Escape closes and hands focus back to the trigger,
 * and every option is a real button, so Tab and Enter work with no key handling of our own.
 */
export default function LanguageSwitch() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<Array<HTMLButtonElement | null>>([]);
  const current = OPTIONS.find((option) => option.value === lang) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    // The first press of an arrow key should land somewhere sensible: the current language.
    const index = OPTIONS.findIndex((option) => option.value === lang);
    options.current[index < 0 ? 0 : index]?.focus();

    const closeOnOutside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [open, lang]);

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) {
      return;
    }
    const buttons = options.current.filter((node): node is HTMLButtonElement => node !== null);
    if (buttons.length === 0) {
      return;
    }
    const here = buttons.findIndex((node) => node === document.activeElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : here < 0
            ? 0
            : (here + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    event.preventDefault();
    buttons[next]?.focus();
  };

  return (
    <div className="lang-switch" ref={container} data-tour="language">
      <button
        type="button"
        ref={trigger}
        className="lang-switch__button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Language: ${current.name}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Globe size={15} aria-hidden="true" className="lang-switch__globe" />
        <Flag lang={current.value} />
        {/* The name where there is room for it, the code where there is not. */}
        <span className="lang-switch__name-inline">{current.name}</span>
        <span className="lang-switch__code">{current.label}</span>
        <ChevronDown size={14} aria-hidden="true" className="lang-switch__chevron" />
      </button>

      {open ? (
        <ul className="lang-switch__menu" onKeyDown={onMenuKeyDown}>
          {OPTIONS.map((option, index) => (
            <li key={option.value}>
              <button
                type="button"
                ref={(node) => {
                  options.current[index] = node;
                }}
                className={option.value === lang ? 'lang-switch__option lang-switch__option--active' : 'lang-switch__option'}
                lang={option.value}
                aria-current={option.value === lang ? 'true' : undefined}
                onClick={() => {
                  setOpen(false);
                  setLang(option.value);
                }}
              >
                <Flag lang={option.value} />
                <span className="lang-switch__name">{option.name}</span>
                <span className="lang-switch__option-code">{option.label}</span>
                {option.value === lang ? <Check size={15} aria-hidden="true" /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
